import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatCard } from '@angular/material/card';
import { environment } from 'src/environments/environment';
import { UserStateService } from 'src/app/services/signals/user-state.service';
import { User } from 'src/app/models/user';
import { ExpirationService } from 'src/app/services/expiration-service';
import { SettingsService } from 'src/app/services/settings.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { MatIconModule } from '@angular/material/icon';
import {
  FaIconComponent,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { MatButtonModule } from '@angular/material/button';
import { CookieConsentService } from 'src/app/services/cookie-consent.service';
import { MatDialog } from '@angular/material/dialog';
import { CookiePopupComponent } from '../cookie-popup/cookie-popup.component';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';
import { Organization } from 'src/app/models/organization.model';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatCard,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    NgxSpinnerModule,
    MatIconModule,
    FontAwesomeModule,
    FaIconComponent,
    MatButtonModule,
  ],
})
export class LoginComponent implements OnInit {
  version: string = environment.version;
  loginForm!: UntypedFormGroup;
  authenticationInvalid = false;
  expired = false;
  displayLoader = false;
  hide = true;

  public fakey = faKey;

  private spinnerService = inject(NgxSpinnerService);

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private userStateService: UserStateService,
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private cookieConsentService: CookieConsentService,
    private usersService: GumUsersService,
    private gumOrganizationsService: GumOrganizationsService,

    private expirationService: ExpirationService // Injecter le service d'expiration
  ) {}

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      account: ['', Validators.required],
      password: ['', Validators.required],
    });

    this.expired = this.expirationService.isSessionExpired();

    // Vérifie si le consentement est manquant dès le chargement
    if (!this.cookieConsentService.hasConsent()) {
      setTimeout(() => {
        this.dialog.open(CookiePopupComponent, {
          disableClose: true,
          width: '700px',
        });
      }, 0); // évite une erreur ExpressionChangedAfterItHasBeenCheckedError
    }
  }

  onSubmit() {
    if (!this.cookieConsentService.hasConsent()) {
      this.dialog.open(CookiePopupComponent, {
        disableClose: true,
        width: '700px',
      });
      return;
    }

    this.displayLoader = true;

    this.authService
      .logon(
        this.loginForm.get('account')?.value,
        this.loginForm.get('password')?.value
      )
      .subscribe({
        next: (isAuthenticated: boolean) => {
          if (isAuthenticated) {
            const user: Partial<User> = {
              account: this.loginForm.get('account')?.value,
              password: '',
              display: this.loginForm.get('account')?.value,
            };

            sessionStorage.setItem('user', JSON.stringify(user));
            this.userStateService.setCurrentUser(user as User);
            this.expirationService.setSessionExpired(false);

            // Étape GUM : get user -> get organisation -> set organisation
            this.usersService.getCurrentUser().subscribe({
              next: (user: User) => {
                console.log('[LOGIN] Utilisateur chargé :', user);

                if (!user.organizationId) {
                  console.warn(
                    '[LOGIN] Aucune organizationId trouvée pour l’utilisateur'
                  );
                  this.router.navigate(['/dashboard']);
                  return;
                }

                this.gumOrganizationsService
                  .getOrganisation(user.organizationId)
                  .subscribe({
                    next: (org: Organization) => {
                      console.log('[LOGIN] Organisation chargée :', org);
                      this.gumOrganizationsService.setCurrentOrganization(org);
                      this.router.navigate(['/dashboard']);
                    },
                    error: (err: any) => {
                      console.error('[LOGIN] Erreur getOrganisation :', err);
                      this.router.navigate(['/dashboard']); // fallback
                    },
                  });
              },
              error: (err: any) => {
                console.error('[LOGIN] Erreur getCurrentUser :', err);
                this.router.navigate(['/dashboard']); // fallback
              },
            });
          } else {
            this.authenticationInvalid = true;
            this.displayLoader = false;
          }
        },
        error: () => {
          this.authenticationInvalid = true;
          this.displayLoader = false;
        },
      });
  }

  forgottenPassword() {
    this.spinnerService.show('spinner');

    this.settingsService.getSettings().subscribe({
      next: (x) => {
        let gumUrl: string | null;
        try {
          gumUrl = x.apps['gum']['url'];
        } catch {
          gumUrl = null;
        }

        if (gumUrl) {
          let parameters = new ExternalParameters();
          parameters.name = 'Nexus';
          parameters.return = `${window.location.origin}/login`;

          // Construire l'URL
          const redirectUrl = `${gumUrl}/forgottenpassword?parameters=${JSON.stringify(
            parameters
          )}`;

          // Log pour déboguer l'URL
          console.log('Redirecting to URL:', redirectUrl);

          // Rediriger l'utilisateur
          window.location.href = redirectUrl;
        } else {
          this.toastr.warning(
            'Votre domaine n’est pas correctement configuré. Veuillez contacter votre administrateur.'
          );
        }
      },
      error: () => {
        this.toastr.error(
          'Erreur lors de la récupération des paramètres. Veuillez contacter votre administrateur.'
        );
      },
      complete: () => {
        this.spinnerService.hide('spinner');
      },
    });
  }
}
